import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Styles from "./HomeEventPopup.module.scss";
import {
  getWalkToTheBoardEventImageUrl,
  WALK_TO_BOARD_CITIES_LINE,
  WALK_TO_BOARD_EVENT_END,
  WALK_TO_BOARD_EVENT_PATH,
  WALK_TO_BOARD_REGISTRATION_URL,
} from "../../constants/walkToTheBoardEvent";

const LEGACY_STORAGE_KEY = "ironlady-walk-to-board-popup-dismissed";
/** After closing the modal once, show right tab; on reload, skip auto-modal but show tab. */
const SESSION_MODAL_CLOSED_KEY = "ironlady-wtb-modal-closed-session";
/** User dismissed the right tab — hide everything until a new browser tab/session. */
const SESSION_HIDE_ALL_KEY = "ironlady-wtb-hide-all-session";

const EVENT_IMAGE = getWalkToTheBoardEventImageUrl();

function readHideAll() {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(SESSION_HIDE_ALL_KEY) === "1";
  } catch {
    return false;
  }
}

export default function HomeEventPopup() {
  const [fullyHidden, setFullyHidden] = useState(readHideAll);
  const [modalOpen, setModalOpen] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);

  useEffect(() => {
    if (new Date() > WALK_TO_BOARD_EVENT_END) return;
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    try {
      if (sessionStorage.getItem(SESSION_HIDE_ALL_KEY) === "1") return;
      if (sessionStorage.getItem(SESSION_MODAL_CLOSED_KEY) === "1") {
        setShowTeaser(true);
        return;
      }
    } catch {
      /* ignore */
    }
    const t = window.setTimeout(() => setModalOpen(true), 200);
    return () => window.clearTimeout(t);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setShowTeaser(true);
    try {
      sessionStorage.setItem(SESSION_MODAL_CLOSED_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modalOpen, closeModal]);

  const openModalFromTeaser = () => {
    setModalOpen(true);
  };

  const dismissTeaserOnly = (e) => {
    e.stopPropagation();
    setShowTeaser(false);
    try {
      sessionStorage.setItem(SESSION_HIDE_ALL_KEY, "1");
    } catch {
      /* ignore */
    }
    setFullyHidden(true);
  };

  if (fullyHidden) return null;

  const modal =
    modalOpen &&
    createPortal(
      <div className={Styles.dock}>
        <div
          className={Styles.card}
          role="dialog"
          aria-modal="false"
          aria-labelledby="walk-to-board-title"
        >
          <div className={Styles.hero}>
            <div className={Styles.imageWrap}>
              <img
                src={EVENT_IMAGE}
                alt="Walk to the board — event visual"
                className={Styles.eventImage}
                loading="eager"
                decoding="async"
              />
              <div className={Styles.imageGradient} aria-hidden="true" />
            </div>
            <button
              type="button"
              className={Styles.close}
              onClick={closeModal}
              aria-label="Close"
            >
              <span className={Styles.closeIcon} aria-hidden="true">
                ×
              </span>
            </button>
          </div>

          <div className={Styles.body}>
            <div className={Styles.bodyInner}>
              <p className={Styles.kicker}>National leadership movement</p>
              <p className={Styles.eyebrow}>Upcoming event</p>
              <h2 id="walk-to-board-title" className={Styles.title}>
                Walk to the Board
              </h2>
              <p className={Styles.lead}>
                Join a powerful network of women leaders.
              </p>

              <div className={Styles.schedule}>
                <div className={Styles.scheduleItem}>
                  <span className={Styles.scheduleLabel}>Date</span>
                  <span className={Styles.scheduleValue}>
                    Sunday, 19 April 2026
                  </span>
                </div>
                <div className={Styles.scheduleDivider} aria-hidden="true" />
                <div className={Styles.scheduleItem}>
                  <span className={Styles.scheduleLabel}>Time</span>
                  <span className={Styles.scheduleValue}>9:00 AM – 12:00 PM</span>
                </div>
              </div>

              <div className={Styles.ctaRow}>
                <a
                  href={WALK_TO_BOARD_REGISTRATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={Styles.cta}
                >
                  <span className={Styles.ctaText}>Register now →</span>
                </a>
                <Link
                  to={WALK_TO_BOARD_EVENT_PATH}
                  className={Styles.eventPageLink}
                  onClick={closeModal}
                >
                  View event page →
                </Link>
              </div>

              <div className={Styles.postCtaBlock}>
                <p className={Styles.postCtaTagline}>
                  7 Cities. One Movement. Join from yours.
                </p>
                <p className={Styles.postCtaCitiesLine}>
                  {WALK_TO_BOARD_CITIES_LINE}
                </p>
                <p className={Styles.postCtaNote}>
                  Hosted in these cities only — select yours when you register.
                </p>
              </div>

              <p className={Styles.footnote}>
                Secure checkout opens in a new tab
              </p>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );

  const teaser =
    showTeaser &&
    !modalOpen &&
    createPortal(
      <div className={Styles.teaserWrap}>
        <button
          type="button"
          className={Styles.teaserDismiss}
          onClick={dismissTeaserOnly}
          aria-label="Hide event reminder"
        >
          ×
        </button>
        <button
          type="button"
          className={Styles.teaser}
          onClick={openModalFromTeaser}
          aria-haspopup="dialog"
        >
          <span className={Styles.teaserText}>Walk to the Board</span>
          <span className={Styles.teaserSub}>Apr 19 · 9am–12pm</span>
          <span className={Styles.teaserHint}>Open</span>
        </button>
      </div>,
      document.body
    );

  return (
    <>
      {modal}
      {teaser}
    </>
  );
}
